import {forwardRef, useCallback, useEffect, useRef, useState} from 'react'
import {RandomToken} from 'Functions'
import API from 'Class/API'
import IconButton from 'Components/UI/IconButton'
import TextField from 'Components/UI/Field/TextField'
import styles from './backendItem.module.scss'

export const BackendItemField = forwardRef(({
    className = '',
    disabled = false,
    community = 0,
    endpoint = '',
    id = '',
    label = '',
    onChange = () => {},
    placeholder = '',
    multiple = false,
    value = {}
}, ref) => {
    const [error, setError] = useState('')
    const [filter, setFilter] = useState('')
    const [loading, setLoading] = useState(false)
    const [results, setResults] = useState(null)
    const [selection, setSelection] = useState({})
    const debounceRef = useRef(null)
    const inputRef = useRef(null)
    const tokenRef = useRef(RandomToken())

    useEffect(() => {
        setError('')
        setResults({})
        debounceRef.current && clearTimeout(debounceRef.current)
        if (!filter) {
            setLoading(false)
            return
        }
        setLoading(true)
        debounceRef.current = setTimeout(() => search(filter), 300)
    }, [filter])

    useEffect(() => {
        if (typeof value !== 'object' || value === null) {
            return
        }
        if (multiple) {
            setSelection(value)
        } else {
            const [id, title] = Object.entries(value)[0] || []
            id && setSelection({[id]: title})
        }
    }, [multiple, value])

    const addItem = useCallback((itemId, itemTitle) => {
        const nextSelection = multiple ? {...selection} : {}
        nextSelection[itemId] = itemTitle
        setSelection(nextSelection)
        setFilter('')
        onChange?.(null, {...nextSelection}, id)
    }, [id, multiple, onChange, selection])

    const removeItem = useCallback((itemId) => {
        const nextSelection = {...selection}
        delete nextSelection[itemId]
        setSelection(nextSelection)
        onChange?.(null, {...nextSelection}, id)
    }, [id, onChange, selection])

    const search = useCallback(async (filter) => {
        if (!filter || !endpoint) {
            setLoading(false)
            return
        }
        try {
            const {list, error} = await API.RequestAsync(endpoint, {
                community,
                exclude: Object.keys(selection),
                filter
            }) || {}

            if (!list || error) {
                setError(error || 'An unknown error occurred')
            } else {
                setResults(list)
            }
        } catch (error) {
            setError(error?.message || 'An unknown error occurred')
        } finally {
            setLoading(false)
        }
    }, [endpoint, selection])

    ref = {
        Input: inputRef.current
    }

    let resultsContent = ''
    if (results && results.length) {
        resultsContent = results.map(({id, title}) => (
            <button
                key={id}
                className={styles.result}
                onClick={() => addItem(id, title)}
            >
                {title}
            </button>
        ))
    } else if (loading) {
        resultsContent = (
            <div className={styles.message}>
                Searching...
            </div>
        )
    } else if (filter) {
        resultsContent = (
            <div className={styles.message}>
                No items matched the filter.
            </div>
        )
    }

    const selectionItems = Object.entries(selection)

    return (
        <div
            className={[
                'Field',
                styles.field,
                disabled && 'Disabled',
                error && 'Error',
                className
            ].filter(Boolean).join('')}
        >
            {label ? <label htmlFor={tokenRef.current}>{label}</label> : ''}
            {selectionItems.length > 0 && (
                <div className={styles.items}>
                    {selectionItems.map(([id, title]) => (
                        <div key={id} className={styles.item}>
                            {title}
                            <IconButton
                                disabled={disabled}
                                feather={'X'}
                                onClick={() => removeItem(id)}
                                title={'Remove this item'}
                            />
                        </div>
                    ))}
                </div>
            )}
            {(multiple || selectionItems.length === 0) && (
                <>
                    <TextField
                        disabled={disabled}
                        id={tokenRef.current}
                        label={''}
                        loading={loading}
                        onChange={(_, newFilter) => setFilter(newFilter)}
                        onInput={(_, newFilter) => setFilter(newFilter)}
                        placeholder={placeholder}
                        ref={inputRef}
                        value={filter}
                    />
                    {resultsContent && (
                        <div className={styles.results}>
                            {resultsContent}
                        </div>
                    )}
                </>
            )}
        </div>
    )
})

export default BackendItemField